import { FC, useEffect, useMemo } from "react";
import { TextResources } from "@portal/assets/text/TextResources";
import { SelectBox, SelectOption } from "../../atoms/selectBox";
import { IPortfolioSelectProps } from "./types";
import { CreateAsk } from "./components/CreateAsk";
import Spinner from "@portal/components/atoms/v2/spinner";
import useIsCorporateUser from "@portal/hooks/useIsCorporateUser";
import { AvatarComponent } from "@portal/components/atoms/avatar";
import { IPortfolio } from "@portal/types/api/IPortfolio";
import { IPowerOfAttorneyInfo } from "@portal/types/api/IPowerOfAttorneyInfo";
import { isPortfolioManaged } from "@portal/helpers/isPortfolioManaged";
import Typography from "@portal/components/atoms/v2/typography";
import Icon from "@portal/components/atoms/icons";

export const hasFullPoaAccess = (
  portfolio: IPortfolio,
  poa: IPowerOfAttorneyInfo[] | undefined,
  selectedUserId: string | undefined,
) => {
  const poaObject = poa?.find(p => p.contactInfo.customerNumber === selectedUserId);
  const portfolioWithPoa = poaObject?.portfolios.find(po => po.portfolioId === portfolio.portfolioId);
  return portfolioWithPoa?.powerOfAttorneyType === "Full" || portfolioWithPoa?.powerOfAttorneyType === "Child";
};

const PortfolioSelect: FC<IPortfolioSelectProps> = ({
  value,
  onChange,
  portfolios,
  portfoliosIsLoading,
  selectRef,
  label,
  labelVariant = "DescriptionBold",
  typographyVariant,
  spaced,
  isAskDisabled = false,
  isManagedDisabled = false,
  createAskAccount = false,
  error = false,
}) => {
  const variant = typographyVariant ? typographyVariant : "Body";
  const { isCorporateUser } = useIsCorporateUser();

  const portfoliosSorted = useMemo(() => {
    if (!portfolios) return [];
    return isAskDisabled || isManagedDisabled
      ? [...portfolios].sort(pf =>
          (isAskDisabled && pf.taxationType === "ASK") || (isManagedDisabled && isPortfolioManaged(pf.groupId)) ? 1 : -1,
        )
      : portfolios;
  }, [isAskDisabled, isManagedDisabled, portfolios]);

  useEffect(() => {
    if (portfolios && portfolios.length === 1 && !value) {
      onChange(portfolios[0]);
    }
  }, [portfolios, onChange, value]);

  const headerPlaceHolder = () => {
    return portfoliosIsLoading ? (
      <div className="flex">
        <Spinner size="small" />
      </div>
    ) : (
      <span className="truncate">
        <Typography variant={variant}>{TextResources.ACCOUNT_SELECT}</Typography>
      </span>
    );
  };

  const renderPortfolio = (portfolio?: IPortfolio, disabled?: boolean) => {
    return (
      <div className={`flex flex-col space-y-1 ${portfolio?.status === "E" ? "opacity-80" : ""}`}>
        <div className="flex items-center space-x-1">
          <AvatarComponent
            small
            text={!isPortfolioManaged(portfolio?.groupId) ? portfolio?.taxationType : "P"}
            disabled={disabled}
          />
          <span className={`truncate ${disabled ? "text-greyBorder" : ""}`}>
            <Typography variant={variant}>
              {portfolio?.name ? `${portfolio?.name} - ${portfolio?.portfolioId}` : portfolio?.portfolioId}
            </Typography>
          </span>
        </div>
        {portfolio?.status === "E" && (
          <div className="flex items-center space-x-1 text-red dark:text-white">
            <div className="w-7.5 flex justify-center">
              <Icon name="xCircleIcon" size="small" />
            </div>
            <Typography variant="Description">{TextResources.ACCOUNT_INACTIVE}</Typography>
          </div>
        )}
        {portfolio?.status.toUpperCase() === "S" && (
          <div className="flex items-center space-x-1">
            <div className="w-7.5 flex justify-center">
              <Icon name="xCircleIcon" size="small" />
            </div>
            <Typography variant="Description">{TextResources.ACCOUNT_BLOCKED}</Typography>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-w-full">
      <Typography variant={labelVariant}>{label}</Typography>
      <SelectBox
        buttonRef={selectRef}
        value={value}
        header={renderPortfolio(value || undefined)}
        headerPlaceholder={headerPlaceHolder()}
        onChange={onChange}
        spaced={spaced}
        error={error}
      >
        <>{createAskAccount && !isCorporateUser && <CreateAsk spaced={spaced} variant={variant} />}</>
        <>
          {portfoliosSorted.map(portfolio => {
            const disabled =
              (isAskDisabled && portfolio.taxationType === "ASK") || (isManagedDisabled && isPortfolioManaged(portfolio.groupId));
            return (
              <SelectOption
                key={portfolio.portfolioId}
                selectKey={portfolio.portfolioId}
                value={portfolio}
                spaced={spaced}
                disabled={disabled}
              >
                {renderPortfolio(portfolio, disabled)}
              </SelectOption>
            );
          })}
        </>
      </SelectBox>
    </div>
  );
};

export default PortfolioSelect;
